import * as React from "react";

import {produce} from "immer";

import * as journal from "@client/features/journal";

import * as router from "@client/router";

import {calculateMealMetrics, getRoundedCalculations} from "@client/calculations";
import {EntryMetrics} from "@client/components/EntryMetrics";
import {useMealEditor} from "@client/components/Stage";
import {server} from "@reactivated";

import {buildCalendar} from "./Journal";

export const UpdateMeal = router.UpdateMeal.withHooks((props) => {
    const mealEditor = useMealEditor({
        target: {
            type: "DAY",
            day: props.resolved.journal.day,
            currentMeal: {
                name: props.resolved.update_meal.name,
                type: props.resolved.update_meal.type,
                hour: props.resolved.update_meal.hour,
            },
        },
        method: "now",
    });

    return {
        ...buildCalendar(props.resolved.journal.day),
        mealEditor,
    };
})
    .withIndex((props) => (
        <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            {props.mealEditor.mealSelector}
            {props.profile.has_hidden_macros === false && (
                <EntryMetrics
                    calculations={getRoundedCalculations(
                        calculateMealMetrics(props.resolved.update_meal),
                        props.profile.use_kilojoules,
                    )}
                />
            )}
        </div>
    ))
    .withActions((props) => {
        const deleteMeal = {
            icon: "remove" as const,
            title: "Delete",
            onClick: () =>
                props.dispatch(
                    journal.confirmAndDeleteMeal(props.resolved.update_meal),
                ),
        };
        const saveMeal = {
            icon: "save" as const,
            title: "Save",
            onClick: () => {
                const result = props.mealEditor.validateMeal();

                if (result == false) {
                    return;
                }

                const updatedDay = produce(props.resolved.journal.day, (draft) => {
                    const sourceMealDraft = draft.meals.find(
                        (meal) => meal.uuid === props.resolved.update_meal.uuid,
                    )!;
                    const sourceMealIndex = draft.meals.findIndex(
                        (m) => m.uuid === props.resolved.update_meal.uuid,
                    );
                    const targetMealDraft = draft.meals.find((meal) =>
                        journal.mealMatches(meal, result),
                    );

                    // Check if we're renaming to an existing meal
                    if (
                        targetMealDraft &&
                        targetMealDraft.uuid !== sourceMealDraft.uuid
                    ) {
                        // Move all entries from source meal to existing target meal
                        targetMealDraft.entries.push(...sourceMealDraft.entries);
                        // Remove the source meal
                        draft.meals.splice(sourceMealIndex, 1);
                    } else {
                        // Just rename the meal
                        sourceMealDraft.name = result.name;
                        sourceMealDraft.type = result.type;
                        sourceMealDraft.hour = result.hour;

                        // If it's an AUTO_CREATED meal, re-sort by removing and re-adding
                        if (result.type === "AUTO_CREATED") {
                            draft.meals.splice(sourceMealIndex, 1);
                            journal.addMealToDayDraft(draft, sourceMealDraft);
                        }
                    }
                });

                const sourceMealRemoved =
                    updatedDay.meals.find(
                        (m) => m.uuid === props.resolved.update_meal.uuid,
                    ) == null;

                if (sourceMealRemoved) {
                    props.notify("Entries moved to existing meal");
                } else {
                    props.notify("Meal updated");
                }

                void server.journal.day.update_day(
                    {user_id: updatedDay.user_id, key: updatedDay.key},
                    {
                        ...updatedDay,
                        deleted_meal_uuids: sourceMealRemoved
                            ? [props.resolved.update_meal.uuid]
                            : [],
                    },
                );

                router.push(
                    "journal.journal",
                    {key: updatedDay.key},
                    {journal: {...props.resolved.journal, day: updatedDay}},
                );
            },
        };
        const copyMeal = {
            icon: "copy" as const,
            title: "Copy",
            onClick: () =>
                props.dispatch(journal.pickDateAndCopyMeal(props.resolved.update_meal)),
        };
        const moveMeal = {
            icon: "scissors" as const,
            title: "Cut",
            onClick: () =>
                props.dispatch(journal.pickDateAndMoveMeal(props.resolved.update_meal)),
        };
        const createRecipe = {
            icon: "compress" as const,
            title: "Create Recipe",
            link: router.reverse("create_recipe_from_meal.index", {
                key: props.resolved.journal.day.key,
                mealUUID: props.resolved.update_meal.uuid,
            }),
        };

        if (props.calendar == null) {
            return [deleteMeal, createRecipe, saveMeal];
        } else {
            return [
                copyMeal,
                createRecipe,
                deleteMeal,
                moveMeal,
                {
                    icon: "share",
                    title: "Share",
                    onClick: async () => {
                        const result = await server.journal.nutrition.publish_meal({
                            meal_uuid: props.resolved.update_meal.uuid,
                        });
                        if (result.type === "success") {
                            props.dispatch({
                                type: "SHARE_SUCCESS",
                                share: {
                                    type: "meal",
                                    share_uuid: props.resolved.update_meal.share_uuid,
                                },
                            });
                        }
                    },
                },
                saveMeal,
            ];
        }
    })
    .withOptions(() => ({
        isDelegatable: true,
        title: "Update Meal",
        back: "Journal",
    }));
